import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Descriptions, Tag, Spin, Alert, Button, Statistic, Row, Col, Typography, Badge } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { EditOutlined, ArrowLeftOutlined, GiftOutlined, TrophyOutlined, CalendarOutlined, FireOutlined } from '@ant-design/icons';
import { all_routes } from '../../../../router/all_routes';
import referralCampaignService from '../../../../services/super-admin/referral-campaign.service';
import { ReferralCampaign, CampaignStats } from '../../../../types/referral.types';
import { CodeType, RewardType } from '../../../../types/referral.types';
import { formatRewardValue, getRewardTypeLabel } from '../../../../utils/referralUtils';
import moment from 'moment';
import FooterSection from '../../../shared/components/footerSection';

const { Title, Text } = Typography;

const CampaignDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<ReferralCampaign | null>(null);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [campaignData, statsData] = await Promise.all([
        referralCampaignService.getCampaignById(id!),
        referralCampaignService.getStats().then(data => data.find(s => s.campaignId === id))
      ]);
      setCampaign(campaignData);
      setStats(statsData || null);
    } catch (err: any) {
      setError('Kampanya detayları yüklenirken bir hata oluştu.');
      console.error('Error fetching campaign details:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        {/* Page Wrapper */}
        <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
          <div className="content flex-fill h-100">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          </div>
          <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
            <FooterSection />
          </div>
        </div>
        {/* /Page Wrapper */}
      </>
    );
  }

  if (error || !campaign) {
    return (
      <>
        {/* Page Wrapper */}
        <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
          <div className="content flex-fill h-100">
            <Alert
              message="Hata"
              description={error || 'Kampanya bulunamadı.'}
              type="error"
              showIcon
              action={
                <Button onClick={() => navigate(all_routes.superAdminReferralCampaigns)}>
                  Geri Dön
                </Button>
              }
            />
          </div>
          <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
            <FooterSection />
          </div>
        </div>
        {/* /Page Wrapper */}
      </>
    );
  }

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper vh-100 d-flex flex-column">
        <div className="content flex-fill" style={{ overflowY: 'auto', paddingBottom: '20px' }}>
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
            <div className="my-auto mb-2">
              <h2 className="mb-1" style={{ color: '#7338a1', fontWeight: 'bold' }}>
                <GiftOutlined style={{ color: '#7338a1', marginRight: '8px' }} />
                {campaign.name}
              </h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminDashboard2}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminReferralCampaigns}>Referans Kampanyaları</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Kampanya Detayları
                  </li>
                </ol>
              </nav>
            </div>
            <div className="head-icons ms-2">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(all_routes.superAdminReferralCampaigns)}
                className="me-2"
                size="large"
                style={{
                  borderRadius: '8px',
                  fontWeight: 500
                }}
              >
                Geri Dön
              </Button>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate(all_routes.superAdminReferralCampaignEdit.replace(':id', campaign.id))}
                size="large"
                style={{
                  height: '48px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 100%)',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(115, 56, 161, 0.3)',
                }}
              >
                Düzenle
              </Button>
            </div>
          </div>
          {/* /Breadcrumb */}

          {/* Hero Section */}
          <Card
            style={{
              background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 50%, #c77dff 100%)',
              borderRadius: '16px',
              padding: '32px 24px',
              marginBottom: '24px',
              border: 'none',
              boxShadow: '0 8px 24px rgba(115, 56, 161, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
            bodyStyle={{ padding: 0 }}
          >
            {/* Dekoratif arka plan */}
            <div style={{
              position: 'absolute',
              top: '-80px',
              right: '-80px',
              width: '250px',
              height: '250px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-60px',
              left: '-60px',
              width: '200px',
              height: '200px',
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <Row gutter={[24, 24]} align="middle">
                <Col xs={24} md={16}>
                  <div style={{ color: 'white' }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      marginBottom: '16px',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <GiftOutlined style={{ fontSize: '40px', color: 'white' }} />
                    </div>
                    <Title level={2} style={{ color: 'white', marginBottom: '8px', fontWeight: 'bold' }}>
                      {campaign.name}
                    </Title>
                    {campaign.description && (
                      <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
                        {campaign.description}
                      </Text>
                    )}
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div style={{ textAlign: 'right' }}>
                    <Badge 
                      status={campaign.isActive ? 'success' : 'default'} 
                      text={
                        <Tag 
                          color={campaign.isActive ? 'success' : 'default'}
                          style={{ 
                            borderRadius: '8px',
                            fontWeight: 600,
                            fontSize: '16px',
                            padding: '8px 16px',
                            border: 'none',
                            background: campaign.isActive ? 'rgba(82, 196, 26, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                            color: 'white'
                          }}
                        >
                          {campaign.isActive ? 'Aktif' : 'Pasif'}
                        </Tag>
                      }
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </Card>

          {/* Kampanya Bilgileri */}
          <Card
            style={{
              borderRadius: '16px',
              border: 'none',
              boxShadow: '0 4px 16px rgba(115, 56, 161, 0.1)',
              marginBottom: '24px'
            }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GiftOutlined style={{ color: '#7338a1' }} />
                <span style={{ fontWeight: 600, color: '#262626' }}>Kampanya Bilgileri</span>
              </div>
            }
          >
            <Descriptions column={{ xs: 1, sm: 2 }} bordered>
              <Descriptions.Item label={<span style={{ fontWeight: 600 }}>Durum</span>}>
                <Tag 
                  color={campaign.isActive ? 'success' : 'default'}
                  style={{ borderRadius: '6px', fontWeight: 500, padding: '4px 12px' }}
                >
                  {campaign.isActive ? 'Aktif' : 'Pasif'}
                </Tag>
              </Descriptions.Item>
              {/* v2.0: Kod türü bilgisi kaldırıldı - artık her şirket için kalıcı, çok kullanımlı kodlar var */}
              <Descriptions.Item label={<span style={{ fontWeight: 600 }}>Kod Sistemi</span>}>
                <Tag 
                  color="purple"
                  style={{ borderRadius: '6px', fontWeight: 500, padding: '4px 12px' }}
                >
                  Şirket Bazlı Kalıcı Kodlar
                </Tag>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#8c8c8c' }}>
                  Her şirket bu kampanya için kendi kalıcı kodunu oluşturur ve istediği kadar kullanabilir.
                </div>
              </Descriptions.Item>
              <Descriptions.Item label={<span style={{ fontWeight: 600 }}>Oluşturulma Tarihi</span>}>
                <div>
                  <CalendarOutlined style={{ marginRight: '4px', color: '#8c8c8c' }} />
                  <Text>{moment(campaign.createdAt).format('DD.MM.YYYY HH:mm')}</Text>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label={<span style={{ fontWeight: 600 }}>Güncelleme Tarihi</span>}>
                <div>
                  <CalendarOutlined style={{ marginRight: '4px', color: '#8c8c8c' }} />
                  <Text>{moment(campaign.updatedAt).format('DD.MM.YYYY HH:mm')}</Text>
                </div>
              </Descriptions.Item>
              {campaign.expiresAt && (
                <Descriptions.Item label={<span style={{ fontWeight: 600 }}>Bitiş Tarihi</span>}>
                  <div>
                    <CalendarOutlined style={{ marginRight: '4px', color: '#ff4d4f' }} />
                    <Text style={{ color: '#ff4d4f', fontWeight: 500 }}>
                      {moment(campaign.expiresAt).format('DD.MM.YYYY HH:mm')}
                    </Text>
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

      {/* Ödül Bilgileri */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} md={12}>
          <Card
            style={{
              borderRadius: '16px',
              border: 'none',
              boxShadow: '0 4px 16px rgba(115, 56, 161, 0.1)',
              background: 'linear-gradient(135deg, rgba(115, 56, 161, 0.05) 0%, rgba(157, 78, 221, 0.02) 100%)'
            }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TrophyOutlined style={{ color: '#7338a1' }} />
                <span style={{ fontWeight: 600, color: '#262626' }}>Yeni Şirket Ödülü</span>
              </div>
            }
          >
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ 
                fontSize: '32px', 
                fontWeight: 'bold', 
                color: '#7338a1', 
                marginBottom: '12px',
                background: 'linear-gradient(135deg, #7338a1 0%, #9d4edd 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {formatRewardValue(campaign.newCompanyRewardType, campaign.newCompanyRewardValue)}
              </div>
              <Tag 
                color="purple" 
                style={{ 
                  borderRadius: '6px', 
                  fontWeight: 500,
                  padding: '6px 16px',
                  fontSize: '14px'
                }}
              >
                {getRewardTypeLabel(campaign.newCompanyRewardType)}
              </Tag>
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            style={{
              borderRadius: '16px',
              border: 'none',
              boxShadow: '0 4px 16px rgba(245, 158, 11, 0.1)',
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(251, 191, 36, 0.02) 100%)'
            }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FireOutlined style={{ color: '#f59e0b' }} />
                <span style={{ fontWeight: 600, color: '#262626' }}>Referans Eden Şirket Ödülü</span>
              </div>
            }
          >
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ 
                fontSize: '32px', 
                fontWeight: 'bold', 
                color: '#f59e0b', 
                marginBottom: '12px'
              }}>
                {formatRewardValue(campaign.referringCompanyRewardType, campaign.referringCompanyRewardValue)}
              </div>
              <Tag 
                color="gold" 
                style={{ 
                  borderRadius: '6px', 
                  fontWeight: 500,
                  padding: '6px 16px',
                  fontSize: '14px'
                }}
              >
                {getRewardTypeLabel(campaign.referringCompanyRewardType)}
              </Tag>
            </div>
          </Card>
        </Col>
      </Row>

      {/* İstatistikler */}
      {stats && (
        <Card
          style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 16px rgba(115, 56, 161, 0.1)',
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FireOutlined style={{ color: '#7338a1' }} />
              <span style={{ fontWeight: 600, color: '#262626' }}>Kampanya İstatistikleri</span>
            </div>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card
                style={{
                  borderRadius: '12px',
                  border: '1px solid #f0f0f0',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(115, 56, 161, 0.05) 0%, rgba(157, 78, 221, 0.02) 100%)'
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <Statistic
                  title={<span style={{ color: '#8c8c8c', fontSize: '14px' }}>Toplam Üretilen</span>}
                  value={stats.totalGenerated}
                  valueStyle={{ color: '#7338a1', fontWeight: 'bold', fontSize: '24px' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card
                style={{
                  borderRadius: '12px',
                  border: '1px solid #f0f0f0',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(82, 196, 26, 0.05) 0%, rgba(52, 211, 153, 0.02) 100%)'
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <Statistic
                  title={<span style={{ color: '#8c8c8c', fontSize: '14px' }}>Toplam Kullanılan</span>}
                  value={stats.totalUsed}
                  valueStyle={{ color: '#52c41a', fontWeight: 'bold', fontSize: '24px' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card
                style={{
                  borderRadius: '12px',
                  border: '1px solid #f0f0f0',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(251, 191, 36, 0.02) 100%)'
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <Statistic
                  title={<span style={{ color: '#8c8c8c', fontSize: '14px' }}>Dönüşüm Oranı</span>}
                  value={stats.conversionRate}
                  precision={2}
                  suffix="%"
                  valueStyle={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '24px' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card
                style={{
                  borderRadius: '12px',
                  border: '1px solid #f0f0f0',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(24, 144, 255, 0.05) 0%, rgba(64, 169, 255, 0.02) 100%)'
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <Statistic
                  title={<span style={{ color: '#8c8c8c', fontSize: '14px' }}>Bekleyen</span>}
                  value={stats.totalGenerated - stats.totalUsed}
                  valueStyle={{ color: '#1890ff', fontWeight: 'bold', fontSize: '24px' }}
                />
              </Card>
            </Col>
          </Row>
        </Card>
      )}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3" style={{ flexShrink: 0 }}>
          <FooterSection />
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default CampaignDetails;

